import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Endpoint, EntityName } from "app/app.type";
import { environment } from "environments/environment";
import moment from "moment";
import { unparse } from "papaparse";
import { Observable } from "rxjs";

@Injectable({
	providedIn: "root",
})
export class SharedService {
	url = environment.url;
	private httpClient = inject(HttpClient);

	/**
	 * @description Busca entidade do banco pelo ID
	 * @param {(Endpoint | string)} entity Endpoint da entidade
	 * @param {string} id identificador da entidade
	 * @return {*} Observable com a entidade buscada
	 */
	getEntity<T>(entity: Endpoint | string, id: string): Observable<T> {
		return this.httpClient.get<T>(`${this.url}${entity}/${id}`);
	}

	/**
	 * @description Busca entidades do banco
	 * @param {(Endpoint | string)} entity Endpoint da entidade
	 * @param {*} [params] parâmetros adicionais para a requisição
	 * @example params = { page: 1, size: 10 }
	 * @return {*} Observable com a lista de entidades
	 */
	getEntities<T>(entity: Endpoint | string, params?: any): Observable<T[]> {
		return this.httpClient.get<T[]>(`${this.url}${entity}`, { params });
	}

	/**
	 * @description Cria uma nova entidade no banco
	 * @param {(Endpoint | string)} entity Endpoint da entidade
	 * @param {T} data Dados da entidade a ser criada
	 * @return {*} Observable com a entidade criada
	 */
	postEntity<T>(entity: Endpoint | string, data: T): Observable<T> {
		return this.httpClient.post<T>(`${this.url}${entity}/create`, data);
	}

	simplePostEntity<T>(entity: Endpoint | string, data: T): Observable<T> {
		return this.httpClient.post<T>(`${this.url}${entity}`, data);
	}

	simplePatchEntity<T>(
		entity: Endpoint | string,
		id: string | number,
		data: Partial<T>,
	): Observable<T> {
		return this.httpClient.patch<T>(`${this.url}${entity}/${id}`, data);
	}

	simplePutEntity<T>(
		entity: Endpoint | string,
		data: any,
	): Observable<T> {
		return this.httpClient.put<T>(`${this.url}${entity}`, data);
	}

	/**
	 * @description Atualiza uma entidade existente no banco
	 * @param {(Endpoint | string)} entity Endpoint da entidade
	 * @param {string} id identificador da entidade
	 * @param {Partial<T>} data Dados da entidade a ser atualizada
	 * @return {*} Observable com a entidade atualizada
	 */
	patchEntity<T>(
		entity: Endpoint | string,
		id: string | number,
		data: Partial<T>,
	): Observable<T> {
		return this.httpClient.patch<T>(`${this.url}${entity}/update/${id}`, data);
	}

	/**
	 * @description Deleta uma entidade do banco
	 * @param {(Endpoint | string)} entity Endpoint da entidade
	 * @param {(string | number)} id identificador da entidade
	 * @return {*}  {Observable<T>} Observable com a confirmação da exclusão
	 */
	deleteEntity<T>(
		entity: Endpoint | string,
		id: string | number,
	): Observable<T> {
		return this.httpClient.delete<T>(`${this.url}${entity}/delete/${id}`);
	}

	/**
	 * @description Deleta uma entidade usando DELETE direto (sem /delete/ na URL)
	 * @param {(Endpoint | string)} entity Endpoint da entidade
	 * @param {(string | number)} id identificador da entidade
	 * @return {*}  {Observable<T>} Observable com a confirmação da exclusão
	 */
	directDeleteEntity<T>(
		entity: Endpoint | string,
		id: string | number,
	): Observable<T> {
		return this.httpClient.delete<T>(`${this.url}${entity}/${id}`);
	}

	/**
	 * @description Busca initiatives paginadas com filtros
	 * @param {number} page Número da página (começando em 0 no frontend)
	 * @param {number} size Tamanho da página
	 * @param {any} filters Filtros aplicados (search, mainCategory, status, etc.)
	 * @return {*} Observable com os dados paginados
	 */
	getInitiativesPaged(
		page: number,
		size: number,
		filters: any = {},
	): Observable<any> {
		const params = {
			page: page + 1, // Backend expects 1-based pagination
			limit: size, // Backend uses 'limit' instead of 'size'
			...filters,
		};

		return this.httpClient.get<any>(`${this.url}${Endpoint.INITIATIVES}`, {
			params,
		});
	}

	exportInitiatives(filters: any = {}): Observable<any> {
		return this.httpClient.get<any>(`${this.url}${Endpoint.INITIATIVES}/export`, {
			params: filters,
		});
	}

	/**
	 * @description Gera um CSV para importação de templates
	 * @param {string[]} fields Lista com os campos que irão compor as colunas do csv
	 * @param {EntityName} entityName Nome da entidade, será utilizada como título do arquivo
	 * @todo Procurar um lugar melhor, uma vez que o SharedService se demonstra ser o local para requisições HTTP
	 */
	generateCSV(fields: string[], entityName: EntityName): void {
		const csv = unparse([fields, []], { delimiter: ";" });
		const blob = new Blob([csv], { type: "text/csv" });
		const url = window.URL.createObjectURL(blob);
		const link = document.createElement("a");

		link.setAttribute("href", url);
		link.setAttribute(
			"download",
			`${entityName}-${moment().toDate().toLocaleDateString("pt-BR")}.csv`,
		);
		link.click();
	}
}
